import type { Post } from '@/@types/post'
import { MEDIA_META } from '@/constants/api'
import { getMediaMetaQueryKey } from '@/utils/fetchers/media'
import { getAxiosInstance } from '@/utils/request'
import { useQuery } from '@tanstack/react-query'
import { isEqual, merge } from 'lodash-es'
import { memo, type MouseEvent } from 'react'
import { useInView } from 'react-intersection-observer'
import ImageGridSection from '../ImageGridSection'
import TextContent from '../TextContent'
import LinkCard from './PostTargetCard/LinkCard'
import PostVideo from './PostVideo'

export interface PostContentProps {
	data: Post
	mode?: 'simple' | 'full'
	onLinkClick?: (url: URL, e: MouseEvent<HTMLElement, globalThis.MouseEvent>) => void
}

export default memo(function PostContent({ data, mode = 'simple', ...props }: PostContentProps) {
	const isSimpleMode = mode === 'simple'
	const post = merge(
		{},
		{
			urlsInText: [],
		},
		data
	) as Post

	const { ref: videoRef, inView: isVideoInView } = useInView()

	const { data: videoURL } = useQuery({
		queryKey: getMediaMetaQueryKey(post.id),
		queryFn: async () =>
			getAxiosInstance()
				.post(
					MEDIA_META,
					{},
					{
						params: {
							id: post.id,
							type: post.type,
						},
					}
				)
				.then((res) => res.data.url),
		enabled: isVideoInView && !!post.id && !!post.video,
	})

	return (
		<>
			<TextContent
				content={data.content}
				urlsInText={data.urlsInText}
				autoCollapsable={mode === 'simple'}
			/>

			{post.linkInfo && <LinkCard data={post.linkInfo} />}
			{post.pictures && <ImageGridSection data={post.pictures} mode="full" />}

			{post.video && <PostVideo src={videoURL} poster={post.video.thumbnailUrl} ref={videoRef} />}
		</>
	)
}, isEqual)
